import type * as THREE from 'three';
import { clamp } from './math';
import type { EnemyState, LockScreenPoint } from './types';

/**
 * Project a world point to CSS px lock reticle (top-left origin).
 */
export function projectLockScreen(
  camera: THREE.Camera,
  worldPos: THREE.Vector3,
  width: number,
  height: number,
  from: THREE.Vector3,
): LockScreenPoint | null {
  const ndc = worldPos.clone().project(camera);
  if (ndc.z < -1 || ndc.z > 1 || ndc.x < -1.2 || ndc.x > 1.2 || ndc.y < -1.2 || ndc.y > 1.2) {
    return null;
  }
  const x = (ndc.x * 0.5 + 0.5) * width;
  const y = (-ndc.y * 0.5 + 0.5) * height;
  const dist = from.distanceTo(worldPos);
  const size = clamp(56 - dist * 0.1, 30, 72);
  return { x, y, size };
}

/**
 * Score foes for lock / soft HUD: prefer near screen center, then nearer to the ship.
 * Acquire (default): engage-phase only. Preview: any living foe.
 */
export function pickBestLockEnemy(
  camera: THREE.Camera,
  ship: THREE.Object3D,
  enemies: readonly EnemyState[],
  opts?: { preview?: boolean },
): EnemyState | null {
  if (enemies.length === 0) {
    return null;
  }
  const preview = opts?.preview === true;
  let best: EnemyState | null = null;
  let bestScore = Infinity;
  for (const enemy of enemies) {
    if (!preview && enemy.phase !== 'engage') continue;
    const ndc = enemy.mesh.position.clone().project(camera);
    const offCenter = Math.hypot(ndc.x, ndc.y);
    const dist = ship.position.distanceTo(enemy.mesh.position);
    // Behind camera / outside clip is a poor lock
    const behindPenalty = ndc.z < -1 || ndc.z > 1 ? 40 : 0;
    // Prefer already-engageable when previewing a mixed wave
    const approachPenalty = preview && enemy.phase !== 'engage' ? 12 : 0;
    const score = offCenter * 28 + dist * 0.08 + behindPenalty + approachPenalty;
    if (score < bestScore) {
      bestScore = score;
      best = enemy;
    }
  }
  return best;
}

/** Live pin only if still in the enemy list and in engage phase. */
export function resolveLiveLockTarget(
  lockTarget: EnemyState | null,
  enemies: readonly EnemyState[],
): EnemyState | null {
  if (!lockTarget) {
    return null;
  }
  const byMesh = enemies.find((e) => e.mesh === lockTarget.mesh);
  if (!byMesh || byMesh.phase !== 'engage') {
    return null;
  }
  return byMesh;
}
